import { SelectContent, SelectItem, SelectTrigger, SelectValue } from '@HoudiniUI/Select';
import { useAdminAccountQuery, useIntegrationAvailableTagsQuery } from '@Queries/index';
import * as Api from '@ViewModels';
import {
	MultipleSelectContent,
	MultipleSelectItem,
	MultipleSelectTrigger,
	MultipleSelectValue,
} from '@WebComponents/MultipleSelect';
import React from 'react';
import { Button } from '../../../../../../../../web/components-v2/Button';
import { SimpleTagSelector } from '../../../../../../../../web/components/entities/tags/SimpleTagSelector';
import { EditModeMultipleSelect, EditModeSelect, EditModeToggle, useEditMode } from '../context';
import { getProviderConfig } from '../providers';
import { styleSheet } from '../styles';
import { SettingGroup } from './SettingGroup';
import { SettingRow } from './SettingRow';
import { SettingsFieldRenderer } from './SettingsFieldRenderer';
import { WritebackSettingsGrid } from './WritebackSettingsGrid';
import { isNorthwesternMutualBrokerDealer } from '../../../../../../../../admin/utils';

interface IMainSettingsGroupProps {
	settings: Api.IIntegrationSettings;
	impersonationContext: Api.IImpersonationContext;
	configuration: Api.IIntegrationConfiguration;
	provider: Api.IntegrationProvider;
	setSettings: (settings: Api.IIntegrationSettings) => void;
	originalIntegration: Api.IExternalIntegration;
}

export const MainSettingsGroup: React.FC<IMainSettingsGroupProps> = ({
	settings,
	impersonationContext,
	configuration,
	provider,
	setSettings,
	originalIntegration,
}) => {
	const [isTagSelectorModalOpen, setIsTagSelectorModalOpen] = React.useState(false);
	const availableTagsQuery = useIntegrationAvailableTagsQuery({
		impersonationContext,
		integrationProvider: provider,
	});

	const accountId = impersonationContext.account?.id ?? '';
	const accountQuery = useAdminAccountQuery({ accountId, enabled: !!accountId });
	const isNorthwesternMutual = isNorthwesternMutualBrokerDealer(accountQuery.data);

	const { isEditing } = useEditMode();

	const providerConfig = getProviderConfig(provider);
	const mainSchema = providerConfig.main;

	const onUpdate = (updatedSettings: Partial<Api.IIntegrationSettings> | Record<string, unknown>) => {
		setSettings({
			...settings,
			...updatedSettings,
		} as Api.IIntegrationSettings);
	};

	const writebacksFieldDisplay = React.useMemo(() => {
		if (!settings.writeBacks?.length) return 'None selected';
		return settings.writeBacks.join(', ');
	}, [settings.writeBacks]);

	const segmentByTagsFieldDisplay = React.useMemo(() => {
		let displayValue = '';
		if (settings.segmentByTags?.enabled) {
			const { containsAll, exclude } = settings.segmentByTags;
			if (containsAll && exclude) {
				displayValue = 'Exclude contacts with all tags:';
			} else if (containsAll) {
				displayValue = 'Include contacts with all tags:';
			} else if (exclude) {
				displayValue = 'Exclude contacts with any of these tags:';
			}
		}
		if (settings.segmentByTags?.enabled && settings.segmentByTags?.tagValues?.length > 0) {
			displayValue += ` ${settings.segmentByTags.tagValues.join(', ')}`;
		}

		return displayValue;
	}, [settings.segmentByTags]);

	const getOriginalSettingValue = <T,>(key: string): T | undefined => {
		return (originalIntegration.settings as unknown as Record<string, unknown>)[key] as T | undefined;
	};

	const onConfirmTagBySegments = (containsAll: boolean, tagValues: string[], exclude: boolean) => {
		onUpdate({
			segmentByTags: {
				containsAll,
				enabled: true,
				exclude,
				tagValues,
			},
		});
		setIsTagSelectorModalOpen(false);
	};

	if (availableTagsQuery.isPending) return <div>Loading...</div>;
	const availableTags = availableTagsQuery?.data ?? [];

	return (
		<>
			<SettingGroup title='Main Settings'>
				{/* Extra fields from config (rendered first if they exist) */}
				{mainSchema?.extraFields?.map(field => (
					<SettingsFieldRenderer
						key={field.key}
						field={field}
						settings={settings}
						onUpdate={onUpdate}
						configuration={configuration}
						getOriginalValue={getOriginalSettingValue}
						singleSelectNoneOption
					/>
				))}

				{/* Common: Mapping By */}
				<SettingRow title='Mapping By' subtitle='Controls which role is used to establish ownership connections'>
					<EditModeSelect
						value={settings.ownershipRole}
						onValueChange={(value: string) => onUpdate({ ownershipRole: value })}
						displayValue={settings.ownershipRole || 'Select ownership role'}
					>
						<SelectTrigger styles={styleSheet.editableField}>
							<SelectValue placeholder='Select ownership role' />
						</SelectTrigger>
						<SelectContent>
							{configuration.availableOwnershipRoles.map(role => (
								<SelectItem key={role} value={role}>
									{role}
								</SelectItem>
							))}
						</SelectContent>
					</EditModeSelect>
				</SettingRow>

				{/* Common: Segment By Tags */}
				<SettingRow
					title='Segment By Tags'
					subtitle='Controls which contacts do (or do not) get synced based on assigned tags'
				>
					{settings.segmentByTags?.enabled && isEditing ? (
						<Button variant='primary' onClick={() => setIsTagSelectorModalOpen(true)}>
							Manage Tags
						</Button>
					) : null}
					<EditModeToggle
						id='segmentByTags'
						isOn={settings.segmentByTags?.enabled}
						onToggleCheckChanged={checked => onUpdate({ segmentByTags: { enabled: checked } })}
						displayValue={segmentByTagsFieldDisplay || null}
					/>
				</SettingRow>

				{provider === Api.IntegrationProvider.MicrosoftDynamics365Sales && isNorthwesternMutual && (
					<SettingRow
						title='Map by Joint Work'
						subtitle='Override the contact owner with the first Joint Work entry that matches a Levitate user; otherwise keep the Dynamics contact owner'
					>
						<EditModeToggle
							id='mapByJointWork'
							isOn={(settings as Api.IMicrosoftDynamics365SalesSettings).mapByJointWork ?? false}
							onToggleCheckChanged={checked => onUpdate({ mapByJointWork: checked })}
						/>
					</SettingRow>
				)}

				{/* Writebacks - from config */}
				{mainSchema?.showWritebacks ? (
					mainSchema.writebackGrid ? (
						<WritebackSettingsGrid
							schema={mainSchema.writebackGrid}
							settings={settings}
							configuration={configuration}
							displayName={providerConfig.displayName}
							onUpdate={onUpdate}
						/>
					) : (
						<SettingRow title='Writebacks' subtitle='Select activities to be recorded back to the integration'>
							<EditModeMultipleSelect
								value={settings.writeBacks || []}
								onChange={(values: string[]) => onUpdate({ writeBacks: values as Api.IntegrationWriteBackType[] })}
								displayValue={writebacksFieldDisplay}
							>
								<MultipleSelectTrigger styles={styleSheet.editableField}>
									<MultipleSelectValue values={settings.writeBacks || []} placeholder='Select writeback types' />
								</MultipleSelectTrigger>
								<MultipleSelectContent>
									{configuration.availableWriteBackTypes.map(type => {
										const isSelected = settings.writeBacks?.includes(type);
										return (
											<MultipleSelectItem key={type} value={type} selected={isSelected}>
												{type}
											</MultipleSelectItem>
										);
									})}
								</MultipleSelectContent>
							</EditModeMultipleSelect>
						</SettingRow>
					)
				) : null}
			</SettingGroup>

			{isTagSelectorModalOpen && (
				<SimpleTagSelector
					isOpen
					onRequestClose={() => setIsTagSelectorModalOpen(false)}
					tags={availableTags}
					onConfirm={onConfirmTagBySegments}
					initialTags={settings.segmentByTags?.tagValues ?? []}
					initialContainsAll={settings.segmentByTags?.containsAll ?? false}
					initialExclude={settings.segmentByTags?.exclude ?? false}
				/>
			)}
		</>
	);
};
